import { Divider, Stack, Typography } from '@mui/material'
import PageWrapper from 'hoc/PageWrapper'
import { useState } from 'react'
import { TenderSearchFilter, TenderSearchFilters } from './tender-search-filter'
import { useQueryState } from 'react-router-use-location-state'
import { useTendersSearchQuery } from 'services/tender'
import { useTranslation } from 'react-i18next'
import { TablePagination } from 'components/TablePagination'
import { TenderCard } from './tender-card'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { ETenderStatus, ITender } from 'types/tender'

export const initialFilters: TenderSearchFilters = {
  name: '',
  orgName: '',
  startFrom: null,
  startUntil: null,
  status: [],
}

export const TenderSearch = ({
  forInnerSearch,
  onSelectItem,
}: {
  forInnerSearch?: boolean
  onSelectItem?: (tender: ITender) => void
}) => {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<TenderSearchFilters>(initialFilters)

  const rowsPerPage = 10
  const [page, setPage] = useQueryState('page', 0)

  const args = {
    page,
    size: rowsPerPage,
    name: filters.name,
    orgName: filters.orgName,
    startFrom: filters.startFrom,
    startUntil: filters.startUntil,
    status: forInnerSearch ? [ETenderStatus.COMPLETED] : filters.status,
  } as const

  const {
    data: tenders,
    isError,
    isLoading,
  } = useTendersSearchQuery(args, { refetchOnMountOrArgChange: true })

  if (isLoading) return <Loading />

  if (isError) return <Error />

  if (tenders === undefined) {
    return <></>
  }

  const content = (
    <>
      <Stack spacing={3}>
        <Typography fontWeight="bold" variant="h6">
          {t('tender-search')}
        </Typography>
        <Divider flexItem />
        <TenderSearchFilter
          forInnerSearch={forInnerSearch}
          defaultValues={{
            ...filters,
            ...(forInnerSearch && {
              status: [ETenderStatus.WINNER_SELECTED],
            }),
          }}
          setFilters={setFilters}
        />
        <Typography variant="h6" fontWeight="bold">
          {t('searchResult', { result: tenders.info.totalRows })}
        </Typography>
        <Divider flexItem />
        {tenders.items.map((tender) => (
          <TenderCard key={tender.id} onClick={onSelectItem} tender={tender} />
        ))}
      </Stack>
      {tenders.info.totalRows > 0 && (
        <TablePagination
          total={tenders.info.totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
        />
      )}
    </>
  )

  if (forInnerSearch) {
    return content
  }

  return <PageWrapper>{content}</PageWrapper>
}
